import { inject, Injectable } from '@angular/core';
import { SpriMediaWatcherService } from '@spri/services/media-watcher';
import { BehaviorSubject } from 'rxjs';

export interface ScreenSizeInfo {
  width: number;
  height: number;
  isSmall: boolean;
  isMedium: boolean;
  isLarge: boolean;
  isExtraLarge: boolean;
  isIpadLandscape: boolean;
  isIpadPortrait: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

@Injectable({ providedIn: 'root' })
export class SpriScreenSizeService {
  private spriMediaWatcherService = inject(SpriMediaWatcherService);

  private screenSizeInfoSubject = new BehaviorSubject<ScreenSizeInfo>(this.getCurrentScreenSizeInfo());
  public screenSizeInfo$ = this.screenSizeInfoSubject.asObservable();

  constructor() {
    // Listen to window resize events
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', () => {
        this.updateScreenSizeInfo();
      });
    }

    // Listen to media changes from SpriMediaWatcherService
    this.spriMediaWatcherService.onMediaChange$.subscribe(() => {
      this.updateScreenSizeInfo();
    });
  }

  /**
   * Get current screen size information
   */
  getCurrentScreenSizeInfo(): ScreenSizeInfo {
    if (typeof window === 'undefined') {
      return this.getDefaultScreenSizeInfo();
    }

    const width = window.innerWidth;
    const height = window.innerHeight;

    return {
      width,
      height,
      isSmall: width < 768,
      isMedium: width >= 768 && width < 1024,
      isLarge: width >= 1024 && width < 1280,
      isExtraLarge: width >= 1280,
      isIpadLandscape: width >= 1024 && width <= 1280,
      isIpadPortrait: width <= 768 && height >= 1024,
      isMobile: width < 768,
      isTablet: (width >= 768 && width < 1024) || (width <= 768 && height >= 1024),
      isDesktop: width >= 1024,
    };
  }

  /**
   * Update screen size information and notify subscribers
   */
  private updateScreenSizeInfo(): void {
    const screenSizeInfo = this.getCurrentScreenSizeInfo();
    this.screenSizeInfoSubject.next(screenSizeInfo);
  }

  /**
   * Get default screen size info for SSR
   */
  private getDefaultScreenSizeInfo(): ScreenSizeInfo {
    return {
      width: 1024,
      height: 768,
      isSmall: false,
      isMedium: false,
      isLarge: true,
      isExtraLarge: false,
      isIpadLandscape: false,
      isIpadPortrait: false,
      isMobile: false,
      isTablet: false,
      isDesktop: true,
    };
  }

  /**
   * Check if screen is small (for navigation purposes)
   * iPad landscape (1024px) should be treated as large
   */
  isScreenSmall(): boolean {
    const info = this.getCurrentScreenSizeInfo();
    return info.isSmall || (info.isMedium && !info.isIpadLandscape);
  }

  /**
   * Check if screen is iPad landscape
   */
  isIpadLandscape(): boolean {
    return this.getCurrentScreenSizeInfo().isIpadLandscape;
  }

  /**
   * Check if screen is iPad portrait
   */
  isIpadPortrait(): boolean {
    return this.getCurrentScreenSizeInfo().isIpadPortrait;
  }

  /**
   * Get navigation appearance based on screen size
   */
  getNavigationAppearance(): 'default' | 'dense' {
    const info = this.getCurrentScreenSizeInfo();

    // Force navigation to be visible on iPad landscape
    if (info.isIpadLandscape) {
      return 'dense';
    }

    // Handle iPad portrait - use collapsed navigation but keep it visible
    if (info.isIpadPortrait) {
      return 'default';
    }

    // Default logic based on screen size
    return this.isScreenSmall() ? 'default' : 'dense';
  }

  /**
   * Get expected navigation width based on appearance
   */
  getNavigationWidth(appearance: 'default' | 'dense'): number {
    return appearance === 'dense' ? 80 : 225;
  }
}
